import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from "@mui/material";
import { IconInfoCircle, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import Button from "../../../../components/ui/Button";
import Text from "../../../../components/ui/Text";
import { GAME_PAYMENT_MODE } from "../../../../constants/game";
import { useAuth } from "../../../../contexts/AuthContext";

export const Players = ({
  bookingPlayers,
  gameData,
  openAddPlayerModal,
  handleRemovePlayer,
}) => {
  const { currentUser } = useAuth();
  const [isServiceFeeModalOpen, setIsServiceFeeModalOpen] = useState(false);

  // amount_per_player включає сервісну комісію (20% або мінімум €0.80)
  // Розраховуємо комісію: max(amount_per_player / 6, 0.8)
  // Це еквівалентно: max(base_price * 0.2, 0.8) де base_price = amount_per_player / 1.2
  const serviceFeePerUser = Math.max(
    (gameData?.amount_per_player || 0) / 6,
    0.8,
  );
  const cost = (gameData?.amount_per_player * bookingPlayers.length).toFixed(2);
  const userGameCredits = (currentUser?.credits || 0).toFixed(2);
  const gameCredits = Math.min(userGameCredits, cost);
  const totalCost = (cost - gameCredits).toFixed(2);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "8px",
        p: 2,
        mb: 3,
        border: "1px solid #E5E5E5",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Text variant="paragraph" sx={{ fontWeight: 600 }}>
          Players
        </Text>
      </Box>
      <Stack spacing={2}>
        {bookingPlayers.map((player, index) => (
          <Box
            key={player.player_id || index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                backgroundColor: "white",
                borderRadius: "8px",
                border: "1px solid #E5E5E5",
                p: 1,
                flex: 1,
              }}
            >
              <Avatar
                src={player?.profile_image_url}
                sx={{ width: 40, height: 40, fontSize: "16px" }}
              >
                {player.first_name?.[0]?.toUpperCase()}
                {player.last_name?.[0]?.toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Text
                  variant="paragraph"
                  sx={{ fontSize: "16px", fontWeight: 500 }}
                >
                  {player.first_name} {player.last_name}
                </Text>
              </Box>

              {bookingPlayers.length > 1 && player?.id !== currentUser?.id && (
                <IconButton
                  onClick={() => handleRemovePlayer(player.player_id)}
                  size="small"
                  sx={{ color: "#666" }}
                >
                  <IconTrash size={16} />
                </IconButton>
              )}
            </Box>
            <Text variant="paragraph" sx={{ fontWeight: 600 }}>
              €
              {gameData.payment_mode === GAME_PAYMENT_MODE.IN_APP
                ? (gameData?.amount_per_player - serviceFeePerUser).toFixed(2)
                : gameData?.amount_per_player.toFixed(2)}
            </Text>
          </Box>
        ))}
      </Stack>

      {/* Add Player Button */}
      <Button
        variant="tertiary"
        onClick={openAddPlayerModal}
        sx={{
          width: "auto",
          mt: 2,
          fontSize: "16px",
          py: 1.5,
        }}
      >
        + Add another player
      </Button>

      {/* Service Fee */}
      <Box
        sx={{
          borderTop: "1px solid #E5E5E5",
          mt: 2,
          mb: 1,
        }}
      />
      {gameData?.payment_mode === GAME_PAYMENT_MODE.IN_APP && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Text variant="paragraph3" sx={{ color: "#A1A1A1" }}>
                Service fee
              </Text>
              <IconButton
                onClick={() => setIsServiceFeeModalOpen(true)}
                size="small"
                sx={{ p: 0, color: "#A1A1A1" }}
              >
                <IconInfoCircle size={16} />
              </IconButton>
            </Box>
            <Text variant="paragraph3" sx={{ color: "#A1A1A1" }}>
              €{(serviceFeePerUser * bookingPlayers.length).toFixed(2)}
            </Text>
          </Box>
          {gameCredits.toFixed(2) > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1,
              }}
            >
              <Text variant="paragraph3" sx={{ color: "#A1A1A1" }}>
                Game credits
              </Text>
              <Text variant="paragraph3" sx={{ color: "#A1A1A1" }}>
                - €{gameCredits.toFixed(2)}
              </Text>
            </Box>
          )}
          <Box
            sx={{
              borderTop: "1px solid #E5E5E5",
              mb: 2,
              mt: 1,
            }}
          />
        </>
      )}

      {/* Total */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Text variant="paragraph" sx={{ fontWeight: 600 }}>
          Total
        </Text>
        <Text variant="paragraph" sx={{ fontWeight: 600 }}>
          €{totalCost}
        </Text>
      </Box>

      {/* Service Fee Info Modal */}
      <Dialog
        open={isServiceFeeModalOpen}
        onClose={() => setIsServiceFeeModalOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: "12px", m: 2, width: "310px" } },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <Text variant="pageHeader" sx={{ fontSize: "20px" }}>
            Service fee
          </Text>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Text variant="paragraph" sx={{ fontSize: "16px", lineHeight: 1.5 }}>
            The service fee is 20% of the total order value. It has a minimum of
            €0.80.
          </Text>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            variant="secondary"
            onClick={() => setIsServiceFeeModalOpen(false)}
            sx={{ width: "auto" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
